import React, { useState } from 'react';
import { CheckCircle, Loader2, Wand2, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';

export const ABTestResultsDialog = ({ 
  open, 
  onOpenChange, 
  results, 
  loading,
  onApplyWinner,
  campaignId
}) => {
  const [applying, setApplying] = useState(false);
  const isBodyTest = results?.ab_test_type === 'body';

  const handleApplyWinner = async () => {
    if (!campaignId || !results?.winner) return;
    
    setApplying(true);
    try {
      await onApplyWinner(campaignId);
      toast.success(`Winner applied to template ${isBodyTest ? 'body' : 'subject'}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply winner");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <span className="text-blue-600 font-bold">A/B</span>
            Test Results
          </DialogTitle>
          <DialogDescription>
            {results?.campaign_name} - Compare {isBodyTest ? 'body content' : 'subject line'} performance
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : results ? (
          <div className="space-y-4 py-4">
            {/* Winner Banner */}
            {results.winner && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-emerald-800">
                    Winner: Variant {results.winner}
                  </span>
                  <span className="text-emerald-600 text-sm">
                    ({results.winner_open_rate}% open rate)
                  </span>
                </div>
                {results.winner_content && (
                  <p className="text-sm text-emerald-700 mt-1 italic line-clamp-2">
                    "{results.winner_content}"
                  </p>
                )}
              </div>
            )}
            
            {/* Variants Table */}
            <div className="space-y-3">
              {results.variants?.map((variant) => (
                <div 
                  key={variant.variant} 
                  className={`border rounded-lg p-3 ${
                    variant.variant === results.winner 
                      ? 'border-emerald-300 bg-emerald-50/30' 
                      : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900">
                      Variant {variant.variant}
                      {variant.variant === results.winner && (
                        <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                          WINNER
                        </span>
                      )}
                    </span>
                    <span className="text-sm text-slate-500">
                      {variant.sent} sent
                    </span>
                  </div>
                  {/* Show subject or body preview */}
                  {isBodyTest ? (
                    <div className="text-sm text-slate-600 italic mb-2 bg-slate-50 p-2 rounded border max-h-20 overflow-y-auto">
                      <FileText className="w-3 h-3 inline mr-1" />
                      {variant.body_preview || "No preview"}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600 italic mb-2">
                      "{variant.subject}"
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Opens:</span>
                      <span className="ml-1 font-medium">{variant.opens}</span>
                      <span className="ml-1 text-slate-400">({variant.open_rate}%)</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Clicks:</span>
                      <span className="ml-1 font-medium">{variant.clicks}</span>
                      <span className="ml-1 text-slate-400">({variant.click_rate}%)</span>
                    </div>
                  </div>
                  {/* Open Rate Bar */}
                  <div className="mt-2">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          variant.variant === results.winner 
                            ? 'bg-emerald-500' 
                            : 'bg-blue-400'
                        }`}
                        style={{ width: `${Math.min(variant.open_rate, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Summary */}
            <div className="text-sm text-slate-500 pt-2 border-t">
              Total: {results.total_sent} sent, {results.total_opens} opens, {results.total_clicks} clicks
            </div>
          </div>
        ) : null}
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {results?.winner && results.total_sent > 0 && (
            <Button 
              onClick={handleApplyWinner}
              disabled={applying}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="apply-winner-btn"
            >
              {applying ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Applying...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Wand2 className="w-4 h-4" />
                  Apply Winner to Template
                </span>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ABTestResultsDialog;
